/**
 * PipelineBoard — shared pipeline flow strip + stage drilldown drawer.
 *
 * Extracted from HubMissionControlPage so Mission Control AND the Engineering
 * page render the SAME stage tiles from the SAME `/hub/mission/pipeline`
 * response (HUB-ENG-NORMALIZE #1358 — one vocabulary, one data source; a number
 * shown on two pages can never differ).
 *
 * Drilldown (#1340): each stage tile is a button (role=button, Enter/Space) that
 * opens a right slide-over drawer listing that stage's `items` (oldest-first,
 * capped 25 server-side), stuck rows flagged, each deep-linking to GitHub. A
 * degraded stage (source unavailable — e.g. in-review with no GITHUB_TOKEN)
 * shows the caveat in the drawer instead of an empty list.
 *
 * Reuses the hub drawer discipline (overlay 0.4, right panel, token colors) from
 * the AgentDrawer / QueueDrawer, and the Card/Pill/StatusDot atoms — no new
 * visual language (design contract, both issues).
 */
import React, { Fragment, useEffect, useState } from 'react'
import { formatTime } from '../utils/dateUtils'
import { Icon, Pill, StatusDot } from './components'
import { fmtAgeSecs } from './timeFormat'
import type { HubMissionPipelineResponse, HubMissionPipelineStage, HubPipelineItem } from './api'

// fmtAgeSecs is the shared humanized-duration formatter (web/src/hub/timeFormat.ts);
// re-exported here so existing importers of PipelineBoard keep working.
export { fmtAgeSecs }

/**
 * PipelineBoard — the horizontal stage strip (flow stages + WAITING ON YOU side
 * pool) with per-tile drilldown. Callers wrap it in their own Card/panel; the
 * MC pause-fleet control stays in Mission Control's wrapper.
 */
export function PipelineBoard({ data }: { data: HubMissionPipelineResponse | null }) {
  const [openStage, setOpenStage] = useState<HubMissionPipelineStage | null>(null)
  const stages = data?.stages ?? []

  if (!data) {
    return <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-dim)' }}>unavailable</div>
  }

  return (
    <>
      <div
        className="row"
        style={{ gap: 0, padding: '12px 8px', overflowX: 'auto', alignItems: 'stretch' }}
      >
        {stages.map((s, i) => (
          <Fragment key={s.key}>
            <PipelineStageCell stage={s} onOpen={() => setOpenStage(s)} />
            {i < stages.length - 1 && (
              <div className="row" style={{ alignItems: 'center', padding: '0 2px', color: 'var(--text-dim)' }}>
                <Icon name="arrow-up-right" size={12} style={{ transform: 'rotate(45deg)' }} />
              </div>
            )}
          </Fragment>
        ))}
        {/* WAITING ON YOU — a side pool, not a flow stage: a plain divider (no
            arrow) sets it apart. It's the only tile meaning Joe is the
            bottleneck. */}
        {data.waiting_on_you && (
          <>
            <div style={{ alignSelf: 'stretch', width: 1, background: 'var(--border)', margin: '2px 10px' }} />
            <WaitingPoolCell stage={data.waiting_on_you} onOpen={() => data.waiting_on_you && setOpenStage(data.waiting_on_you)} />
          </>
        )}
      </div>
      {openStage && <PipelineStageDrawer stage={openStage} onClose={() => setOpenStage(null)} />}
      <CacheSyncNote syncedAt={data.cache_synced_at} />
    </>
  )
}

/** cache-as-of honesty stamp (#1423) — every "cache"-sourced stage (all but
 *  in-review) is only as fresh as tenon_github_issues_cache, which fills from
 *  webhooks plus a 30-min reconciliation cron. "refreshed Xs ago" elsewhere on
 *  the page only reflects the client's own poll cadence, not whether the
 *  underlying data changed, which read as "the counters are wrong" when they
 *  were really just current-as-of an older sync. */
function CacheSyncNote({ syncedAt }: { syncedAt: string | null }) {
  if (!syncedAt) return null
  const ageSecs = Math.max(0, Math.floor((Date.now() - new Date(syncedAt).getTime()) / 1000))
  return (
    <div
      className="mono"
      title="MAX(last_synced_at) across the GitHub issues cache — the cache-sourced stages (all but In Review) are only as fresh as this."
      style={{ padding: '0 12px 8px', fontSize: 10, color: 'var(--text-dim)' }}
    >
      cache as of {formatTime(new Date(syncedAt))} ({fmtAgeSecs(ageSecs)} ago)
    </div>
  )
}

/** Keyboard/click affordances shared by both cell kinds — a tile is a button
 *  that opens its drilldown drawer. */
function cellButtonProps(label: string, onOpen: () => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': `Open ${label} details`,
    onClick: onOpen,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() }
    },
  }
}

export function PipelineStageCell({ stage, onOpen }: { stage: HubMissionPipelineStage; onOpen: () => void }) {
  const unavailable = stage.count === null
  return (
    <div
      {...cellButtonProps(stage.label, onOpen)}
      title={stage.measures}
      style={{
        flex: '1 0 120px', minWidth: 120, padding: '4px 10px', cursor: 'pointer',
        borderRadius: 'var(--radius)',
        background: stage.stuck ? 'color-mix(in srgb, var(--err) 12%, transparent)' : 'transparent',
        border: stage.stuck ? '1px solid var(--err)' : '1px solid transparent',
      }}
    >
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {stage.label}
      </div>
      <div className="row" style={{ gap: 6, alignItems: 'baseline', marginTop: 2 }}>
        <span className="mono" style={{ fontSize: 22, color: stage.stuck ? 'var(--err)' : 'var(--text)' }}>
          {unavailable ? '—' : stage.count}
        </span>
        {stage.stuck && <StatusDot tone="err" />}
      </div>
      <div className="mono" style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 1, whiteSpace: 'nowrap' }}>
        {unavailable
          ? (stage.source === 'unavailable' ? 'no source' : '—')
          : stage.oldest_age_secs != null
            ? `oldest ${fmtAgeSecs(stage.oldest_age_secs)}`
            : 'empty'}
      </div>
      {stage.note && (
        <div
          className="mono"
          title="Live GitHub PR search was unavailable, so items already in review couldn't be subtracted — this count may include them."
          style={{ fontSize: 9, color: 'var(--warn)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          ⚠ {stage.note}
        </div>
      )}
    </div>
  )
}

/** WAITING ON YOU side pool (#1341). Visually distinct from the flow stages: a
 *  filled surface with a bell, err-tone when the oldest item is > 7d (stuck).
 *  The one tile that means Joe himself is the bottleneck. */
export function WaitingPoolCell({ stage, onOpen }: { stage: HubMissionPipelineStage; onOpen: () => void }) {
  const stuck = stage.stuck
  const accent = stuck ? 'var(--err)' : 'var(--text-muted)'
  return (
    <div
      {...cellButtonProps(stage.label, onOpen)}
      title={stage.measures}
      style={{
        flex: '1 0 130px', minWidth: 130, padding: '4px 10px', cursor: 'pointer',
        borderRadius: 'var(--radius)',
        background: stuck ? 'var(--err-soft)' : 'var(--surface-2)',
        border: stuck ? '1px solid var(--err)' : '1px solid var(--border)',
      }}
    >
      <div className="row" style={{ gap: 4, alignItems: 'center' }}>
        <Icon name="bell" size={11} style={{ color: accent }} />
        <div style={{ fontSize: 10, color: accent, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {stage.label}
        </div>
      </div>
      <div className="row" style={{ gap: 6, alignItems: 'baseline', marginTop: 2 }}>
        <span className="mono" style={{ fontSize: 22, color: stuck ? 'var(--err)' : 'var(--text)' }}>
          {stage.count ?? '—'}
        </span>
        {stuck && <StatusDot tone="err" />}
      </div>
      <div className="mono" style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 1, whiteSpace: 'nowrap' }}>
        {stage.oldest_age_secs != null ? `oldest ${fmtAgeSecs(stage.oldest_age_secs)}` : 'empty'}
      </div>
    </div>
  )
}

/** Right slide-over listing a stage's drilldown items (#1340). Closes on Escape
 *  or backdrop click. Stuck rows flagged err-tone; each row deep-links to GitHub.
 *  A degraded stage shows the caveat instead of an empty list. */
function PipelineStageDrawer({ stage, onClose }: { stage: HubMissionPipelineStage; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const items = stage.items ?? []
  const degraded = stage.count === null || stage.source === 'unavailable'

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`${stage.label} items`}
        style={{
          width: 520, maxWidth: '92vw', height: '100%',
          background: 'var(--surface)', borderLeft: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div className="row" style={{ padding: '12px 16px', gap: 10, borderBottom: '1px solid var(--border)' }}>
          <Icon name="git-branch" size={14} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{stage.label}</span>
          {stage.count != null && <Pill variant="ghost">{stage.count}</Pill>}
          {stage.stuck && <Pill variant="err">stuck</Pill>}
          <span style={{ flex: 1 }} />
          <button
            onClick={onClose}
            aria-label="Close"
            title="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', fontSize: 16, lineHeight: 1 }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* What this stage measures — always shown so the count is legible. */}
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {stage.measures}
          </div>

          {degraded ? (
            <div style={{
              background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8,
              padding: '12px 14px', fontSize: 12.5, color: 'var(--warn)', lineHeight: 1.5,
            }}>
              ⚠ Source unavailable — this stage can't be listed right now
              {stage.source === 'unavailable' ? ' (live GitHub search is down or GITHUB_TOKEN is unset).' : '.'}
            </div>
          ) : items.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-dim)', padding: '8px 0' }}>
              Nothing in this stage.
            </div>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {items.map((it, i) => (
                <PipelineDrawerRow key={it.pr_number ?? it.issue_number ?? i} item={it} last={i === items.length - 1} />
              ))}
            </div>
          )}
          {stage.note && (
            <div className="mono" style={{ fontSize: 11, color: 'var(--warn)' }}>⚠ {stage.note}</div>
          )}
        </div>
      </div>
    </div>
  )
}

function PipelineDrawerRow({ item, last }: { item: HubPipelineItem; last: boolean }) {
  const num = item.pr_number ?? item.issue_number
  const prefix = item.pr_number ? 'PR #' : '#'
  return (
    <a
      href={item.github_url}
      target="_blank"
      rel="noreferrer"
      className="row"
      style={{
        padding: '9px 12px', gap: 10, textDecoration: 'none',
        borderBottom: last ? 0 : '1px solid var(--border)',
        background: item.stuck ? 'color-mix(in srgb, var(--err) 8%, transparent)' : 'transparent',
      }}
    >
      <StatusDot tone={item.stuck ? 'err' : 'dim'} />
      <span className="mono" style={{ fontSize: 11, color: 'var(--text-dim)', flex: '0 0 56px' }}>
        {prefix}{num}
      </span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.title}
      </span>
      {item.stuck && <Pill variant="err">stuck</Pill>}
      <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-dim)', flex: '0 0 40px', textAlign: 'right' }}>
        {fmtAgeSecs(item.age_in_stage_secs ?? item.age_in_stage_days * 86400)}
      </span>
      <Icon name="arrow-up-right" size={11} style={{ color: 'var(--text-dim)' }} />
    </a>
  )
}
